import type { LinearRGB } from '../../../core/color';
import type { Landmark } from '../../../data/types';
import { PATTERN } from '../../../render/patterns/patternIds';
import { SURFACE } from '../../mesh/attributes';
import type { MeshBuilder } from '../../mesh/meshBuilder';
import { boxSolid } from '../solids';
import { buildConventRange } from './conventRange';
import { buildConventSoil } from './conventSoil';
import type { LandmarkContext } from './landmarkFeature';
import {
  LUCIENERF,
  lucienerfFrame,
  lucienerfPartFrame,
  lucienerfStockCentre,
} from './lucienerfPlan';
import { paintedChapelStyles, type PaintedChapelColors } from './paintedChapels';
import { meshFrameOf } from './planFrame';
import { NO_TRACERY } from './tracery/tracerySink';

/** One early working property; the later warehouse and shipyard are not backdated. */
export function buildLucienerf(
  builder: MeshBuilder,
  landmark: Landmark,
  colors: PaintedChapelColors & {
    readonly tile: LinearRGB;
    readonly timber: LinearRGB;
    readonly ground: LinearRGB;
  },
  ids: { readonly edgeId: number; readonly seed: number },
  context: LandmarkContext
): void {
  const plan = lucienerfFrame(landmark),
    masonry = paintedChapelStyles(colors, ids, NO_TRACERY),
    wall = {
      ...masonry.wall,
      color: colors.timber,
      surface: SURFACE.wood,
      pattern: PATTERN.timberFront1538,
    },
    styles = {
      ...masonry,
      wall,
      roof: {
        ...masonry.roof,
        color: colors.tile,
        surface: SURFACE.roofTile,
        pattern: PATTERN.roofTileHollePan,
      },
    },
    base = context.elevation ?? 0,
    terrain = context.elevationAt ?? (() => base);
  buildConventSoil(
    builder,
    { ...wall, color: colors.ground },
    landmark.footprint,
    terrain,
    base,
    0.025,
    false
  );
  for (const p of LUCIENERF.houses)
    buildConventRange(builder, meshFrameOf(lucienerfPartFrame(plan, p)), styles, p);
  const stock = { ...masonry.opening, color: colors.timber, surface: SURFACE.wood };
  for (const p of LUCIENERF.timber) {
    const frame = meshFrameOf(lucienerfPartFrame(plan, p)),
      y = terrain(lucienerfStockCentre(landmark, p)) - base + 0.045,
      boardWidth = (p.width - (p.boards - 1) * 0.06) / p.boards;
    for (const u of [-p.length * 0.3, p.length * 0.3])
      boxSolid(
        builder,
        stock,
        frame,
        [u - 0.1, y, -p.width / 2],
        [u + 0.1, y + 0.16, p.width / 2],
        { top: true, bottom: true }
      );
    for (let layer = 0; layer < p.layers; layer++)
      for (let board = 0; board < p.boards; board++) {
        const v = -p.width / 2 + board * (boardWidth + 0.06),
          bottom = y + 0.16 + layer * 0.17;
        boxSolid(
          builder,
          stock,
          frame,
          [-p.length / 2, bottom, v],
          [p.length / 2, bottom + 0.14, v + boardWidth],
          { top: true, bottom: true }
        );
      }
  }
}
